<!--
  - SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->
<template>
	<div id="assistant_prefs" class="section">
		<h2>
			<AssistantIcon />
			{{ t('assistant', 'Nextcloud Assistant') }}
		</h2>
		<div id="assistant-content">
			<NcFormGroup :label="t('assistant', 'Select which features you want to enable')"
				:hide-label="true"
				class="switch-group">
				<NcFormBox>
					<NcFormBoxSwitch v-if="state.assistant_available"
						:model-value="state.assistant_enabled"
						@update:model-value="onCheckboxChanged($event, 'assistant_enabled')">
						{{ t('assistant', 'Nextcloud Assistant in header') }}
					</NcFormBoxSwitch>
					<NcFormBoxSwitch v-if="state.audio_chat_available"
						:model-value="state.autoplay_audio_chat"
						@update:model-value="onCheckboxChanged($event, 'autoplay_audio_chat')">
						{{ t('assistant', 'Auto-play audio chat responses') }}
					</NcFormBoxSwitch>
					<NcFormBoxSwitch v-if="state.free_prompt_picker_available"
						:model-value="state.free_prompt_picker_enabled"
						@update:model-value="onCheckboxChanged($event, 'free_prompt_picker_enabled')">
						{{ t('assistant', 'AI text generation in smart picker') }}
					</NcFormBoxSwitch>
					<NcFormBoxSwitch v-if="state.text_to_image_picker_available"
						:model-value="state.text_to_image_picker_enabled"
						@update:model-value="onCheckboxChanged($event, 'text_to_image_picker_enabled')">
						{{ t('assistant', 'AI image generation in smart picker') }}
					</NcFormBoxSwitch>
					<NcFormBoxSwitch v-if="state.text_to_sticker_picker_available"
						:model-value="state.text_to_sticker_picker_enabled"
						@update:model-value="onCheckboxChanged($event, 'text_to_sticker_picker_enabled')">
						{{ t('assistant', 'AI sticker generation in smart picker') }}
					</NcFormBoxSwitch>
					<NcFormBoxSwitch v-if="state.speech_to_text_picker_available"
						:model-value="state.speech_to_text_picker_enabled"
						@update:model-value="onCheckboxChanged($event, 'speech_to_text_picker_enabled')">
						{{ t('assistant', 'AI transcription in smart picker') }}
					</NcFormBoxSwitch>
				</NcFormBox>
			</NcFormGroup>
			<NcNoteCard v-if="noProvidersAvailable" type="warning">
				{{ t('assistant', 'No suitable providers are available. They must first be enabled by your administrator.') }}
			</NcNoteCard>
			<div v-else>
				<h3>{{ t('assistant', 'Configured backends') }}</h3>
				<p>{{ t('assistant', 'The following services are used as backends for Nextcloud Assistant:') }}</p>
				<div v-for="(taskNames, providerName) in providers" :key="providerName">
					<h5>
						{{ providerName }}
					</h5>
					{{ taskNames.join(', ') }}
				</div>
			</div>
			<div v-if="rememberedConversations.length > 0">
				<h3>{{ t('assistant', 'Remembered conversations') }}</h3>
				<p>{{ t('assistant', 'The following conversations are remembered by the Assistant Chat and will be taken into account for every new conversation:') }}</p>
				<NcFormBox>
					<NcFormBoxButton v-for="conversation in rememberedConversations"
						:key="conversation.id"
						:label="conversation.title"
						:description="conversation.summary">
						<template #icon>
							<MemoryIcon />
						</template>
					</NcFormBoxButton>
				</NcFormBox>
			</div>
		</div>
	</div>
</template>

<script>
import AssistantIcon from './icons/AssistantIcon.vue'

import NcFormGroup from '@nextcloud/vue/components/NcFormGroup'
import NcFormBox from '@nextcloud/vue/components/NcFormBox'
import NcFormBoxSwitch from '@nextcloud/vue/components/NcFormBoxSwitch'
import NcFormBoxButton from '@nextcloud/vue/components/NcFormBoxButton'
import NcNoteCard from '@nextcloud/vue/components/NcNoteCard'

import MemoryIcon from 'vue-material-design-icons/Memory.vue'

import { loadState } from '@nextcloud/initial-state'
import { generateUrl } from '@nextcloud/router'
import axios from '@nextcloud/axios'
import { showSuccess, showError } from '@nextcloud/dialogs'

export default {
	name: 'PersonalSettings',

	components: {
		AssistantIcon,
		NcFormGroup,
		NcFormBox,
		NcFormBoxSwitch,
		NcFormBoxButton,
		NcNoteCard,
		MemoryIcon,
	},

	props: [],

	data() {
		return {
			state: loadState('assistant', 'config'),
			providers: loadState('assistant', 'availableProviders'),
			rememberedConversations: loadState('assistant', 'rememberedSessions'),
		}
	},

	computed: {
		noProvidersAvailable() {
			return this.state.text_to_image_picker_available === false
				&& this.state.task_processing_available === false
				&& this.state.speech_to_text_picker_available === false
		},
	},

	watch: {
	},

	mounted() {
	},

	methods: {
		onCheckboxChanged(newValue, key) {
			this.state[key] = newValue
			this.saveOptions({ [key]: this.state[key] ? '1' : '0' })
		},
		saveOptions(values) {
			const req = {
				values,
			}
			const url = generateUrl('/apps/assistant/config')
			return axios.put(url, req)
				.then((response) => {
					showSuccess(t('assistant', 'Assistant options saved'))
				})
				.catch((error) => {
					showError(
						t('assistant', 'Failed to save assistant options')
						+ ': ' + error.response?.request?.responseText,
					)
				})
		},
	},
}
</script>

<style scoped lang="scss">
#assistant_prefs {
	#assistant-content {
		margin-left: 40px;
	}

	h2 {
		display: flex;
		align-items: center;
		justify-content: start;
		gap: 8px;
	}

	.line,
	.settings-hint {
		display: flex;
		align-items: center;
		margin-top: 12px;
		.icon {
			margin-right: 4px;
		}
	}

	.switch-group {
		max-width: 800px;
	}

	.checkbox-text {
		display: flex;
		flex-direction: row;

		.icon {
			margin-right: 8px;
			margin-left: 24px;
		}
	}

	.line {
		> label {
			width: 300px;
			display: flex;
			align-items: center;
		}
		> input {
			width: 300px;
		}
	}
}
</style>
